import React, { useState } from 'react';
import axios from 'axios';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import {
  GRUNTWORX_CONNECTION,
  DISCONNECT_GRUNTWORX,
} from 'constants/constants';
import ConfirmationModal from 'common/ConfirmationModal';
import { AlertMessage } from 'utilities/utils';
import gruntworxIcon from 'images/gruntworx.png';
import { useGruntWorxExport } from 'common/GruntWorx/useGruntWorxExport';

const initialData = {
  gruntworxKey: '',
  loading: false,
  apiKeyModal: false,
  apiKeySaveModal: false,
  errors: {},
};

const Gruntworx = (props) => {
  const [data, setData] = useState(initialData);
  const { connectionStatus, checkGruntworxConnection } = useGruntWorxExport();

  const handleValidation = () => {
    const err = {};
    let isValid = true;
    if (!data.gruntworxKey) {
      isValid = false;
      err.gruntworxKey = 'Enter GruntWorx API key';
    }
    setData({ ...data, errors: err });
    return isValid;
  };

  const handleApiKey = async (value = '') => {
    if (value === 'connect') {
      if (handleValidation()) {
        setData(prev => ({ ...prev, loading: true }));
        try {
          const request = await axios.post(GRUNTWORX_CONNECTION, {
            gruntworx_key: data.gruntworxKey,
          });
          if (request.data.success) {
            setData({
              ...data,
              apiKeyModal: false,
              gruntworxKey: '',
              errors: {},
              loading: false,
            });
            AlertMessage('success', 'GruntWorx connected successfully!', 3000);
            // Refresh connection status after successful connection
            await checkGruntworxConnection();
          } else {
            setData({
              ...data,
              loading: false,
              errors: { api: request.data.message },
            });
          }
        } catch (err) {
          setData({
            ...data,
            loading: false,
            errors: { api: 'Connection failed' },
          });
        }
      }
    } else if (value === 'disconnect') {
      try {
        await axios.delete(DISCONNECT_GRUNTWORX);
        setData({
          ...data,
          loading: false,
          gruntworxKey: '',
          apiKeySaveModal: false,
        });
        AlertMessage('success', 'GruntWorx disconnected successfully', 3000);
        // Refresh connection status after successful disconnection
        await checkGruntworxConnection();
      } catch (err) {
        setData({ ...data, loading: false });
        console.log(err);
      }
    }
  };

  const handleChange = (field, value = '') => {
    if (field === 'apiKeyModal') {
      if (value === 'connect') {
        handleApiKey('connect');
      } else {
        setData({
          ...data,
          apiKeyModal: !data.apiKeyModal,
          gruntworxKey: '',
          errors: {},
        });
      }
    } else {
      setData({ ...data, [field]: value, errors: {} });
    }
  };

  // Determine which button to show based on connection status
  const renderActionButton = () => {
    if (connectionStatus === null) {
      return null; // Still loading initial status
    }

    if (connectionStatus === 'Active') {
      return (
        <div className="d-flex h-100 flex-column justify-content-end">
          <button
            type="button"
            className="btn btn-primary mb-5"
            onClick={() => setData({ ...data, apiKeySaveModal: true })}
          >
            Disconnect
          </button>
        </div>
      );
    }

    return (
      <div className="d-flex h-100 flex-column justify-content-end">
        <button
          type="button"
          className="btn btn-primary mb-5"
          onClick={() => handleChange('apiKeyModal')}
        >
          Connect
        </button>
      </div>
    );
  };

  return (
    <div className="col-lg-4 col-sm-12 col-md-6">
      {data.loading ? <div id="loading" /> : null}
      <div
        className="card mb-4 shadow-s"
        style={{ height: 'calc(100% - 16px)' }}
      >
        <div
          className="card-img-top d-flex justify-content-center"
          style={{ height: '150px', alignItems: 'center' }}
        >
          <img
            src={gruntworxIcon}
            alt="gruntworx_icon"
            style={{ maxWidth: '100%', height: '100%', objectFit: 'contain' }}
          />
        </div>
        <div className="card-body">
          <h5 className="card-title">{props.integrationAppsPref?.label}</h5>
          <div className="h-100">
            {renderActionButton()}
          </div>
        </div>
      </div>

      <Modal
        isOpen={data.apiKeyModal}
        backdrop="static"
        className="modal-dialog modal-md"
      >
        <ModalHeader toggle={() => handleChange('apiKeyModal', '')}>
          GruntWorx Integration Setup
        </ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-md-12">
              <div className="form-group">
                <label htmlFor="gruntworx-key">API Key</label>
                <input
                  type="password"
                  className="form-control"
                  name="gruntworxKey"
                  id="gruntworx-key"
                  value={data.gruntworxKey}
                  onChange={(e) => handleChange('gruntworxKey', e.target.value)}
                  placeholder="Enter your GruntWorx key"
                />
                {data.errors.gruntworxKey && (
                  <span className="text-danger">{data.errors.gruntworxKey}</span>
                )}
                <small className="form-text text-muted">
                  Enter your GruntWorx API key
                </small>
              </div>
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-md-12">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleChange('apiKeyModal', 'connect')}
              >
                Connect
              </button>
              <button
                type="button"
                className="btn btn-secondary ml-2"
                onClick={() => handleChange('apiKeyModal', '')}
              >
                Cancel
              </button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      <ConfirmationModal
        isOpen={data.apiKeySaveModal}
        isHtml
        messageText={
          "<div><h5>Remove access?</h5><p>Liscio will no longer have access to your GruntWorx Account. You'll need to grant access if you want to use this app or service again.</p></div>"
        }
        noBtnClick={() => setData({ ...data, apiKeySaveModal: false })}
        yesBtnClick={() => handleApiKey('disconnect')}
        yesBtnText="Ok"
        noBtnText="Cancel"
        btnClassName="justify-content-start"
      />
    </div>
  );
};

export default Gruntworx; 